const comparisonLabels = {
  origin: 'Origin',
  destination: 'Destination',
  flight_id: 'Flight ID',
  currency: 'Currency',
  cabin_code: 'Cabin',
  class_code: 'Class',
  brand: 'Fare brand',
  adult_fare: 'Adult fare',
  child_fare: 'Child fare',
  infant_fare: 'Infant fare',
  tax: 'Tax',
  free_seats: 'Available seats',
  estimated_ptl: 'Estimated PTL',
  estimated_ttl: 'Estimated TTL'
}

const toMinorUnits = value => {
  if (typeof value !== 'string' && typeof value !== 'number') return null
  const match = String(value).match(/^(-?)(\d+)(?:\.(\d{1,2})0*)?$/)
  if (!match) return null
  const amount = (Number(match[2]) * 100) + Number((match[3] || '').padEnd(2, '0'))
  return Number.isSafeInteger(amount) ? amount * (match[1] ? -1 : 1) : null
}

const formatValue = value => {
  if (value === null || value === undefined || value === '') return 'Not provided'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const formatAmount = (value, currency) => {
  if (toMinorUnits(value) === null || !/^[A-Z]{3}$/.test(currency || '')) return '—'
  return `${currency} ${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const deadlineTime = value => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value)) return NaN
  const calendarDate = value.slice(0, 10)
  const calendar = new Date(`${calendarDate}T00:00:00Z`)
  if (!Number.isFinite(calendar.getTime()) || calendar.toISOString().slice(0, 10) !== calendarDate || Number(value.slice(11, 13)) > 23) return NaN
  return new Date(value).getTime()
}

const formatDeadline = (value, estimatedAt) => {
  const time = deadlineTime(value)
  if (!Number.isFinite(time)) return 'Not provided'
  const date = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Africa/Dar_es_Salaam',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(time))
  const base = deadlineTime(estimatedAt)
  if (!Number.isFinite(base)) return date
  const difference = time - base
  const minutes = Math.floor(Math.abs(difference) / 60000)
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor((minutes % 1440) / 60)
  const remainder = minutes % 60
  const duration = [days ? `${days}d` : '', hours ? `${hours}h` : '', remainder ? `${remainder}m` : ''].filter(Boolean).join(' ') || (difference === 0 ? '0m' : '<1m')
  return `${date} (${difference < 0 ? '-' : ''}${duration})`
}

const normalizeValidation = (payload, request) => {
  const invalid = () => { throw new Error('The flight check returned incomplete or inconsistent data. Please retry validation.') }
  const directions = request.trip_type === 'RT' ? ['outbound', 'inbound'] : ['outbound']
  const comparison = payload && payload.comparison
  if (!payload || typeof payload.snapshot_id !== 'string' || !payload.snapshot_id.trim()
    || payload.search_snapshot_id !== request.search_snapshot_id
    || !Array.isArray(payload.flights) || payload.flights.length !== directions.length
    || payload.count !== directions.length
    || !comparison || typeof comparison.changed !== 'boolean' || typeof comparison.critical !== 'boolean'
    || !Array.isArray(comparison.flights) || comparison.flights.length !== directions.length) invalid()

  const validatePrice = (price, totalField) => {
    if (!price || !/^[A-Z]{3}$/.test(price.currency || '') || toMinorUnits(price[totalField]) === null) invalid()
    const passengerTypes = ['adult', 'child', 'infant']
    passengerTypes.forEach(type => {
      const category = price[type]
      const quantity = request[type === 'adult' ? 'adults' : type]
      if (!category || category.quantity !== quantity
        || !['unitFare', 'unitTax', 'unitTotal', 'total'].every(field => toMinorUnits(category[field]) !== null)) invalid()
    })
  }
  validatePrice(payload.calculatedPrice, 'itineraryTotal')
  const currency = payload.calculatedPrice.currency
  const legs = payload.flights.map((flight, index) => {
    if (!flight) invalid()
    validatePrice(flight.calculatedPrice, 'segmentTotal')
    if (flight.calculatedPrice.currency !== currency) invalid()
    const direction = directions[index]
    const matches = comparison.flights.filter(item => item && item.direction === direction)
    if (matches.length !== 1) invalid()
    const compared = matches[0]
    if (typeof compared.changed !== 'boolean' || !Array.isArray(compared.critical_fields)
      || !compared.differences || typeof compared.differences !== 'object' || Array.isArray(compared.differences)) invalid()
    const fields = Object.keys(compared.differences)
    const criticalFields = ['origin', 'destination', 'flight_id', 'currency']
    if (compared.changed !== (fields.length > 0)
      || compared.critical_fields.some(field => !fields.includes(field))
      || fields.some(field => criticalFields.includes(field) && !compared.critical_fields.includes(field))) invalid()
    const differences = Object.entries(compared.differences).map(([field, values]) => {
      if (!values || !Object.prototype.hasOwnProperty.call(values, 'search')
        || !Object.prototype.hasOwnProperty.call(values, 'get_flight')) invalid()
      const monetary = ['adult_fare', 'child_fare', 'infant_fare', 'tax'].includes(field)
      const deadline = ['estimated_ptl', 'estimated_ttl'].includes(field)
      return {
        field,
        label: comparisonLabels[field] || field,
        critical: compared.critical_fields.includes(field),
        searchValue: monetary ? formatAmount(values.search, request.currency) : deadline ? formatDeadline(values.search) : formatValue(values.search),
        currentValue: monetary ? formatAmount(values.get_flight, currency) : deadline ? formatDeadline(values.get_flight) : formatValue(values.get_flight)
      }
    })
    return { direction, title: direction === 'inbound' ? 'Return' : 'Outbound', flight, differences, changed: compared.changed, critical: compared.critical_fields.length > 0 }
  })
  if (comparison.changed !== legs.some(leg => leg.changed)
    || comparison.critical !== legs.some(leg => leg.critical)
    || (currency !== request.currency && !comparison.critical)) invalid()
  return {
    flightSnapshotId: payload.snapshot_id,
    searchSnapshotId: payload.search_snapshot_id,
    total: payload.calculatedPrice.itineraryTotal,
    currency,
    price: payload.calculatedPrice,
    estimatedAt: payload.estimated_at || null,
    estimatedPtl: payload.estimated_ptl || null,
    estimatedTtl: payload.estimated_ttl || null,
    comparison,
    legs
  }
}

module.exports = { comparisonLabels, toMinorUnits, formatValue, formatAmount, formatDeadline, normalizeValidation }
